/**
 * The ticket `ai_category` vocabulary, in one place.
 *
 * `Ticket.ai_category` (`backend/app/models/ticket.py`:83) is ONE column that two unrelated
 * producers write, on two different token scales:
 *
 *   - the AI classification scale, Title-cased — `Network | Hardware | Software | Email | Security
 *     | Other`, held as a closed set in `ai_service._CATEGORIES` (`services/ai_service.py`:13) and
 *     written by `classify_category` on the email-polling path (`email_poller.py`:1365) and by the
 *     AI settings screen's own classify call (`routers/ai.py`:97). `manager_agent`'s
 *     `_TASK_TO_CATEGORY` (`:770`) maps an analysis `task_type` onto five of those six and is the
 *     only writer that runs off an agent run.
 *   - the maintenance-queue scale, lowercased — `backups | m365 | av_edr | updates | firewall |
 *     email_security | reports`. `move_queue` assigns it straight from the request body
 *     (`routers/tickets.py`:2013 `ticket.ai_category = body.maintenance_category`), so the server
 *     mints whatever the client sends; the set above is the client's own select in
 *     `TicketDetail.jsx`:3635 ("קטגוריית תחזוקה"), the only control that sends this field.
 *
 * The two scales share no token, so one map can hold both without ambiguity, and it has to: the
 * column holds whichever scale last wrote it, so a surface that labels only one scale prints the
 * other's identifiers verbatim. That is what both ticket surfaces did — `TicketsPage`'s row chip
 * (`:1258`) and its tooltip printed the stored token with no lookup at all, and `TicketDetail`'s
 * badge (`:2216`) carried a local map holding the six AI tokens only, so the seven maintenance
 * tokens fell through it. Moving a ticket to the maintenance queue and opening it — a technician
 * action, no flag and no `ANALYSIS_PROVIDER` involved — drew `backups` / `av_edr` /
 * `email_security` as English identifiers inside the Hebrew header, next to the `🔧 בתור תחזוקה`
 * badge, while the Dashboard and the maintenance list drew the same value in Hebrew.
 *
 * `ticketCategoryVocabulary.test.js` re-derives both scales from their producers and reddens if a
 * token appears without a label here.
 */

export const TICKET_CATEGORY_LABEL = {
  // ── AI classification scale — `ai_service._CATEGORIES`, whole ────────────────
  // These six words are the ones the Dashboard card and the maintenance list already use; a second
  // spelling for the same arm on the ticket list is how the two screens drift apart.
  Network: "רשת",
  Hardware: "חומרה",
  Software: "תוכנה",
  Email: "דואר",
  Security: "אבטחה",
  Other: "אחר",

  // ── Maintenance-queue scale — the "move to maintenance" dialog's options ─────
  // The maintenance list's own badge (`MaintenancePage.TICKET_CAT_BADGE`) carries these same seven
  // words, so a ticket that moved to that queue reads the same category on both screens.
  backups: "גיבויים",
  m365: "M365",
  av_edr: "AV/EDR",
  updates: "עדכונים",
  firewall: "חומת אש",
  email_security: "אבטחת מייל",
  reports: "דוחות",
};

/**
 * The label for a token, or — for a token nobody has written a label for yet — the token itself.
 * Echoing an unmapped identifier is the honest failure and keeps it inspectable; what is not
 * allowed is reaching that arm for a token this map is supposed to cover, which the parity guard
 * prevents. `null` for an absent value, so a caller can skip the row rather than draw an empty one.
 */
export function ticketCategoryLabel(value) {
  if (value == null || value === "") return null;
  const token = String(value);
  return TICKET_CATEGORY_LABEL[token] || token;
}
